import { apiEndpoint } from "../utils/env";

/**
 * Discussion on an archived document.
 *
 * Everything here is addressed by <b>(databaseName, archiveNo)</b> rather than by the document row's id.
 * A version chain is several rows sharing one ArchiveNo, so this is what makes a conversation survive a
 * check-in, a new-version upload or a restore — a thread about an invoice is about the invoice, not about
 * the particular draft that was on screen when somebody typed.
 *
 * The database name travels on every call, including the ones that already carry a comment id. The server
 * re-derives access from it each time; an id on its own says nothing about who is allowed to read the
 * document it hangs off.
 */
async function call(path, token, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (options.body) headers["Content-Type"] = "application/json";

  const res = await fetch(apiEndpoint(path), { ...options, headers });
  const txt = await res.text();
  let json = null;
  try { json = txt ? JSON.parse(txt) : null; } catch { json = txt; }

  if (!res.ok) {
    const msg = (json && json.message) || (typeof json === "string" ? json : res.statusText);
    const err = new Error(msg || `Request failed: ${res.status}`);
    err.status = res.status;
    err.code = json && json.code;
    err.details = json;
    throw err;
  }
  return json;
}

const q = encodeURIComponent;

export function listComments(token, databaseName, archiveNo) {
  return call(`/api/Document/Comments?databaseName=${q(databaseName)}&archiveNo=${q(archiveNo)}`, token);
}

/**
 * Comment counts for a page of results, one request for the whole page rather than one per row.
 * The server caps the list, so callers pass a page and not a result set.
 */
export function commentCounts(token, databaseName, archiveNos) {
  const list = (archiveNos || []).filter(Boolean).join(",");
  if (!list) return Promise.resolve({ counts: {} });
  return call(`/api/Document/Comments/Counts?databaseName=${q(databaseName)}&archiveNos=${q(list)}`, token);
}

/**
 * @param mentions user names the author picked. The server keeps only those the body actually names —
 *                 sending a name the text does not contain notifies nobody, by design.
 */
export function addComment(token, { databaseName, archiveNo, body, parentCommentId, documentId, mentions }) {
  return call("/api/Document/Comments", token, {
    method: "POST",
    body: JSON.stringify({ databaseName, archiveNo, body, parentCommentId, documentId, mentions }),
  });
}

export function editComment(token, id, { databaseName, body }) {
  return call(`/api/Document/Comments/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify({ databaseName, body }),
  });
}

/** Tombstones the comment. The row and its text stay in the database; it stops being shown. */
export function deleteComment(token, id, databaseName) {
  return call(`/api/Document/Comments/${id}?databaseName=${q(databaseName)}`, token, { method: "DELETE" });
}

/** Roots only — a thread has one resolved state, and it is not the individual replies'. */
export function resolveComment(token, id, { databaseName, resolved }) {
  return call(`/api/Document/Comments/${id}/Resolve`, token, {
    method: "POST",
    body: JSON.stringify({ databaseName, resolved }),
  });
}
